import {
  HighlightableString,
  makeHighlightableString,
} from '@/app/log-viewer/highlighting/HighlightableString';
import { AppType, isDriverApp, isEaterApp, Log, PlatformType } from '@/parsing/Log';

import {
  LogViewerTableRowModel,
  LogViewerTableRowModelData,
  LogViewerTableRowType,
} from '../../LogViewerTableRowModel';

export interface SummaryRowModel extends LogViewerTableRowModel {
  data: SummaryRowModelData;
}

export interface SummaryRowModelData extends LogViewerTableRowModelData {
  summary: {
    title: HighlightableString;

    deviceInfo: {
      platformType: PlatformType;

      deviceTypeTitle: HighlightableString;
      deviceType: HighlightableString | undefined;

      deviceOsVersionTitle: HighlightableString;
      deviceOsVersion: HighlightableString | undefined;

      deviceNameTitle: HighlightableString;
      deviceName: HighlightableString | undefined;

      appVersionTitle: HighlightableString;
      appVersion: HighlightableString | undefined;
    };

    foundUserIDsTitle: HighlightableString;
    foundUserIDs: {
      userId: HighlightableString;

      mixpanelLink: string | undefined;
      mixpanelLinkTitle: HighlightableString;

      adminPanelLink: string;
      adminPanelLinkTitle: HighlightableString;
    }[];

    foundOrdersTitle: HighlightableString;
    foundOrders: {
      orderId: HighlightableString;

      pickup: HighlightableString;
      destinations: HighlightableString[];

      adminPanelLink: string;
      adminPanelLinkTitle: HighlightableString;

      commsHistoryLink: string;
    }[];
  };
}

export abstract class LogViewerTableSummaryRowModelFactory {
  static makeFromLog(log: Log): SummaryRowModel {
    const data: SummaryRowModelData = {
      type: LogViewerTableRowType.Summary,
      indexBeforeApplyingFilters: 0,
      indexAfterApplyingFilters: 0,
      passesFilter: true,
      isSelected: false,
      isBookmarked: false,

      summary: {
        title: makeHighlightableString('Summary'),
        deviceInfo: {
          platformType: log.deviceInfo.platformType ?? PlatformType.Unknown,

          deviceTypeTitle: makeHighlightableString('Platform:'),
          deviceType: log.deviceInfo.deviceType
            ? makeHighlightableString(log.deviceInfo.deviceType)
            : undefined,

          deviceOsVersionTitle: makeHighlightableString('OS Version:'),
          deviceOsVersion: log.deviceInfo.deviceOsVersion
            ? makeHighlightableString(log.deviceInfo.deviceOsVersion)
            : undefined,

          deviceNameTitle: makeHighlightableString('Device:'),
          deviceName: log.deviceInfo.deviceName
            ? makeHighlightableString(log.deviceInfo.deviceName)
            : undefined,

          appVersionTitle: makeHighlightableString('App Version:'),
          appVersion: log.deviceInfo.appVersion
            ? makeHighlightableString(log.deviceInfo.appVersion)
            : undefined,
        },
        foundUserIDsTitle: makeHighlightableString('Found user IDs:'),
        foundUserIDs: log.foundUserIDs.map((id) => ({
          userId: makeHighlightableString(id),

          mixpanelLink: this.composeMixpanelProfileLink(id, log.deviceInfo.appType),
          mixpanelLinkTitle: makeHighlightableString('Mixpanel'),

          adminPanelLink: this.composeAdminPanelProfileLink(id, log.deviceInfo.appType),
          adminPanelLinkTitle: makeHighlightableString('Admin Panel'),
        })),
        foundOrdersTitle: makeHighlightableString('Found orders:'),
        foundOrders: log.foundOrders.map((foundOrder) => ({
          orderId: makeHighlightableString(`Order ID: ${foundOrder.orderId}`),
          pickup: makeHighlightableString(`Pickup: ${foundOrder.pickup.address}`),
          destinations:
            foundOrder.destinations.length === 1
              ? [makeHighlightableString(`Destination: ${foundOrder.destinations[0].address}`)]
              : foundOrder.destinations.map((d, index) =>
                  makeHighlightableString(`Destination #${index + 1}: ${d.address}`),
                ),
          adminPanelLinkTitle: makeHighlightableString('Admin Panel'),
          adminPanelLink: this.composeAdminPanelOrderLink(foundOrder.orderId, foundOrder.cityId),
          commsHistoryLink: this.composeCommsHistoryLink(foundOrder.orderId, foundOrder.cityId),
        })),
      },
    };

    const model: SummaryRowModel = {
      data,
    };

    return model;
  }

  private static composeCommsHistoryLink(orderID: string, cityID: string): string {
    const filterArg = {
      limit: 25,
      offset: 0,
      filter: {
        order_id: orderID,
        city_id: cityID,
        order_system: 'taxi',
        from: 0,
        to: 0,
      },
    };
    // https://admin-panel.bolt.eu/comms-channels/commsHistory?filter=%7B%22limit%22%3A25%2C%22offset%22%3A0%2C%22filter%22%3A%7B%22order_id%22%3A%221044116943%22%2C%22city_id%22%3A%221%22%2C%22order_system%22%3A%22taxi%22%2C%22from%22%3A0%2C%22to%22%3A0%7D%7D&tz=Europe%2FTallinn
    return `https://admin-panel.bolt.eu/comms-channels/commsHistory?filter=${encodeURIComponent(
      JSON.stringify(filterArg),
    )}`;
  }

  private static composeMixpanelProfileLink(userID: string, appType?: AppType): string | undefined {
    // Mixpanel link cannot be constructed for driver app
    if (appType && isDriverApp(appType)) {
      return undefined;
    }

    if (appType && isEaterApp(appType)) {
      return `https://mixpanel.com/project/2031947/view/32793/app/profile#distinct_id=delivery-${userID}`;
    }

    return `https://mixpanel.com/project/1165336/view/32804/app/profile#distinct_id=client-${userID}`;
  }

  private static composeAdminPanelProfileLink(userID: string, appType?: AppType): string {
    if (appType && isDriverApp(appType)) {
      return `https://admin-panel.bolt.eu/fleet/drivers/edit/${userID}`;
    }
    return `https://admin-panel.bolt.eu/identity/consumers/rider/${userID}/overview`;
  }

  private static composeAdminPanelOrderLink(orderID: string, cityID: string): string {
    return `https://admin-panel.bolt.eu/ride-hailing-order/orders/order/${orderID}/${cityID}`;
  }
}
